import type { VesselCallError } from "@features/vessel-call/vessel-call-status/VesselCallStatus.types.tsx";
import type { CompleteVesselCallByIdArgs } from "@services/vesselCall/types";
import { completeVesselCallById } from "@services/vesselCallApi";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";

const getCompleteVesselCallAction = (status?: string): string => {
  if (status?.includes("APPROVED")) return "approve_vessel_call";
  if (status?.includes("REJECTED")) return "reject_vessel_call";
  return "complete_vessel_call";
};

export const useCompleteVesselCall = ({
  handleSuccess,
  handleError,
}: {
  handleSuccess: (status?: string) => void;
  handleError: (error: VesselCallError) => void;
}) => {
  const completeCallMutation = useMutation({
    mutationFn: (variables: Parameters<typeof completeVesselCallById>[0]) =>
      runTransaction(
        {
          module: "vessel_call",
          screen: "VesselCallStatus",
          action: getCompleteVesselCallAction(variables.params?.status),
          entityType: "vessel_call",
        },
        (transactionId) =>
          completeVesselCallById({ ...variables, ...(transactionId ? { transactionId } : {}) }),
      ),
    onSuccess: async (_data, variables) => {
      const status = variables?.params?.status as string | undefined;
      handleSuccess(status);
    },
    onError: (error: VesselCallError) => handleError(error),
  });

  const completeCall = (
    {
      service,
      vesselCallId,
      params,
    }: {
      service: string;
      vesselCallId: string;
      params: CompleteVesselCallByIdArgs;
    },
    options?: { onError: (error: VesselCallError) => void },
  ) => {
    const variables = { service, vesselCallId, params };
    if (options) {
      completeCallMutation.mutate(variables, options);
    } else {
      completeCallMutation.mutate(variables);
    }
  };

  return {
    completeCall,
  };
};
